import { type ReactNode } from 'react';
import { Alert, Box, Column, Row, Switch, Tooltip, Typography } from '@tempmee/react';
import { type Control, useController } from 'react-hook-form';
import { OutlineInformationCircleIcon } from '@tempmee/react-icons';
import {
  fireShiftRequestAutoconfirmFavoriteSelected,
  fireShiftRequestAutoconfirmEligibleSelected,
  fireEditShiftAutoconfirmFavoriteSelected,
  fireEditShiftAutoconfirmEligibleSelected,
  fireOpenShiftAutoconfirmFavoriteSelected,
  fireOpenShiftAutoconfirmEligibleSelected,
} from '~/posthogs/events';
import { colors } from '@tempmee/tailwind';
import { type TAutoConfirmToggleName } from '~/posthogs/events/ShiftRequest/autoconfirmToggleAttempted';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { useShiftRequestFlowTracking } from '../ShiftRequestFlowTracking';

export type AutoConfirmEventContext = 'shift_request' | 'edit_shift' | 'open_shift';

type TAutoConfirmKey = 'favorites' | 'eligibleProfessionals';

const PROFESSIONS_WITHOUT_AUTO_CONFIRM = ['Associate Dentist', 'Front Desk'];

/**
 * Whether the Auto-Confirm toggles are offered for a given profession/specialty. Auto-Confirm
 * is unavailable for Associate Dentist / Front Desk and for any non-General specialty. Shared
 * so callers can gate experiment defaulting on the same rule the field renders by.
 */
export const isAutoConfirmAvailable = (professionType: string, specialty: string): boolean => {
  if (PROFESSIONS_WITHOUT_AUTO_CONFIRM.includes(professionType)) return false;
  return Boolean(specialty?.toLowerCase()?.includes('general'));
};

/**
 * Why Auto-Confirm is unavailable. The two reasons are not interchangeable: telling an Associate
 * Dentist the feature needs a General specialty implies changing the specialty would unlock it,
 * which it never does for that profession.
 */
const getUnavailableReason = (professionType: string): string =>
  PROFESSIONS_WITHOUT_AUTO_CONFIRM.includes(professionType)
    ? `The Auto-Confirm feature is not available for ${professionType} at this time.`
    : 'The Auto-Confirm feature is only available for shifts without a specialty preference (General).';

interface IAutoConfirmFieldProps {
  control: Control<IShiftDetailsFormValues>;
  professionType: string;
  preferredSpecialty: string;
  eventContext?: AutoConfirmEventContext;
  hideHeader?: boolean;
  disabled?: boolean;
  /** Renders the Favorites toggle inactive (used when the office has no favorites). */
  favoritesDisabled?: boolean;
  /**
   * Last-minute enforcement (GOTU-5399): locks both toggles and swaps the counter-offer note
   * for the alert explaining why. The value itself is set by the container.
   */
  lockedOn?: boolean;
}

const FAVORITES_TOOLTIP = (
  <Typography as="span" bold color={colors.shades[0]}>
    Selecting favorites will send them invites
  </Typography>
);

const ELIGIBLE_TOOLTIP = (
  <Typography as="span" bold color={colors.shades[0]}>
    Professional&apos;s eligibility criteria:
    <br />
    <br />
    {'•'} Has 95% or higher attendance score.
    <br />
    {'•'} Has a minimum of 2 years working experience.
    <br />
    {'•'} Has received exceptional performance ratings on over 95% of completed shifts.
    <br />
    {'•'} Has completed at least 3 shifts.
  </Typography>
);

interface IAutoConfirmToggleProps {
  label: string;
  tooltip: ReactNode;
  checked: boolean;
  onChange: () => void;
  disabled: boolean;
  testId: string;
  /** DS Switch colour scheme — `favorite` for the Favorites toggle. */
  color?: 'favorite';
  /** Reports an opt-out attempt (GOTU-5437). Its presence is what turns on click capture. */
  onLockedAttempt?: () => void;
}

/**
 * Toggle with its tooltip on BOTH the label and the ⓘ icon (GOTU-5392). The icon sits outside
 * the Switch so clicking it reads the tooltip without flipping the toggle.
 */
const AutoConfirmToggle = ({
  label,
  tooltip,
  checked,
  onChange,
  disabled,
  testId,
  color,
  onLockedAttempt,
}: IAutoConfirmToggleProps) => (
  <Row align="center" gap={2}>
    {/*
      A disabled button element fires no click event at all, not even one that bubbles, so the
      switch is taken out of hit-testing and this wrapper catches the click instead. The ⓘ icon
      sits outside, so reading the tooltip is not an attempt.

      Flex, not block: a block wrapper adds 7px of baseline leading around the inline-flex Switch
      and drops the ⓘ 3.5px below centre (GOTU-6904).
    */}
    <Row
      align="center"
      onClick={onLockedAttempt}
      data-testid={`${testId}-toggle-wrapper`}
      data-cy={`${testId}-toggle-wrapper`}
    >
      <Switch
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        color={color}
        className={onLockedAttempt ? 'pointer-events-none' : undefined}
        // The Switch styles string labels only; a node passes through untouched, so its render
        // callback (ui#315) hands out the state and the DS contract is applied here. `bold` has to
        // sit on the element — body1 declares `font-normal`, which beats an inherited weight.
        label={({ checked: isChecked, disabled: isDisabled }) => (
          <Tooltip placement="bottom" title={tooltip}>
            <Typography
              as="span"
              variant="body1"
              bold={isChecked}
              className={isDisabled ? 'text-text-disabled' : 'text-text-primary'}
            >
              {label}
            </Typography>
          </Tooltip>
        )}
        data-testid={`${testId}-toggle`}
        data-cy={`${testId}-toggle`}
      />
    </Row>
    <Tooltip placement="bottom" title={tooltip}>
      <OutlineInformationCircleIcon color={colors.secondary[500]} />
    </Tooltip>
  </Row>
);

export const AutoConfirmField = ({
  control,
  professionType,
  preferredSpecialty,
  eventContext,
  hideHeader = false,
  disabled = false,
  favoritesDisabled = false,
  lockedOn = false,
}: IAutoConfirmFieldProps) => {
  const { field } = useController({ name: 'autoConfirm', control });
  const { recordAutoConfirmToggleAttempt } = useShiftRequestFlowTracking();

  if (!isAutoConfirmAvailable(professionType, preferredSpecialty)) {
    return (
      <Column
        data-testid="auto-confirm-field"
        data-cy="auto-confirm-field"
        style={{ gap: 12 }}
      >
        {!hideHeader && (
          <Row align="center" gap={2}>
            <Typography variant="subtitle1" color={colors.neutral[600]}>
              Auto-Confirm:
            </Typography>
          </Row>
        )}
        <Typography variant="body1"
          italic
          color={colors.neutral[500]}
          data-testid="auto-confirm-unavailable-message"
          data-cy="auto-confirm-unavailable-message"
          style={{ padding: '0 24px' }}
        >
          {getUnavailableReason(professionType)}
        </Typography>
      </Column>
    );
  }

  const fireToggleEvent = (key: TAutoConfirmKey, newValue: boolean) => {
    if (!eventContext) return;

    const payload = {
      autoConfirm: (newValue ? 'on' : 'off') as 'on' | 'off',
      designDate: 202603,
    };

    const eventMap = {
      shift_request: {
        favorites: fireShiftRequestAutoconfirmFavoriteSelected,
        eligibleProfessionals: fireShiftRequestAutoconfirmEligibleSelected,
      },
      edit_shift: {
        favorites: fireEditShiftAutoconfirmFavoriteSelected,
        eligibleProfessionals: fireEditShiftAutoconfirmEligibleSelected,
      },
      open_shift: {
        favorites: fireOpenShiftAutoconfirmFavoriteSelected,
        eligibleProfessionals: fireOpenShiftAutoconfirmEligibleSelected,
      },
    };

    eventMap[eventContext][key](payload);
  };

  const handleToggle = (key: TAutoConfirmKey) => {
    if (disabled || lockedOn) return;
    if (key === 'favorites' && favoritesDisabled) return;

    const current = field.value ?? { favorites: false, eligibleProfessionals: false };
    const newValue = !current[key];
    field.onChange({
      ...current,
      [key]: newValue,
    });
    fireToggleEvent(key, newValue);
  };

  const togglesDisabled = disabled || lockedOn;

  // Only a toggle the experiment holds ON can be "attempted off" — Favorites disabled for want
  // of favorites is a different lock, and clicking it is not an opt-out.
  const lockedAttemptHandler = (key: TAutoConfirmKey, toggle: TAutoConfirmToggleName) =>
    lockedOn && !(key === 'favorites' && favoritesDisabled)
      ? () => recordAutoConfirmToggleAttempt(toggle)
      : undefined;

  return (
    <Column
      data-testid="auto-confirm-field"
      data-cy="auto-confirm-field"
      style={{ padding: '0 24px', gap: 12 }}
    >
      {!hideHeader && (
        <Row align="center" gap={2} style={{ marginBottom: 8 }}>
          <Typography variant="subtitle1" color={colors.neutral[600]}>
            Auto-Confirm:
          </Typography>
        </Row>
      )}

      {lockedOn ? (
        <Alert
          severity="info"
          // DS Alert centres its icon unless it has a title too; ours wraps to two lines.
          className="items-start"
          description="Auto-Confirm is automatically turned on for all last minute postings and does not apply to counter offers."
          data-testid="auto-confirm-last-minute-alert"
          data-cy="auto-confirm-last-minute-alert"
        />
      ) : (
        <Box data-testid="auto-confirm-message" data-cy="auto-confirm-message">
          <Typography variant="body1" color={colors.neutral[500]}>
            Auto-Confirm does not apply to counter offers
          </Typography>
        </Box>
      )}

      <Column
        data-testid="auto-confirm-toggles"
        data-cy="auto-confirm-toggles"
        aria-disabled={togglesDisabled}
        style={{ gap: 12 }}
      >
        <AutoConfirmToggle
          label="Favorites"
          tooltip={FAVORITES_TOOLTIP}
          checked={field.value?.favorites ?? false}
          onChange={() => handleToggle('favorites')}
          disabled={togglesDisabled || favoritesDisabled}
          color="favorite"
          testId="favorites"
          onLockedAttempt={lockedAttemptHandler('favorites', 'favorites')}
        />

        <AutoConfirmToggle
          label="Eligible Professionals"
          tooltip={ELIGIBLE_TOOLTIP}
          checked={field.value?.eligibleProfessionals ?? false}
          onChange={() => handleToggle('eligibleProfessionals')}
          disabled={togglesDisabled}
          testId="eligible"
          onLockedAttempt={lockedAttemptHandler('eligibleProfessionals', 'eligible_professionals')}
        />
      </Column>
    </Column>
  );
};
